import React, { useState } from 'react';
import './AdminShell.css';

// AdminShell — wraps the admin dashboard body in a premium-fintech layout:
//
//   ┌──────────────────┬─────────────────────────────────────┐
//   │ Sidebar nav      │ Sticky top bar (title + actions)    │
//   │ (grouped tabs)   ├─────────────────────────────────────┤
//   │                  │ Main content (the active tab body)  │
//   └──────────────────┴─────────────────────────────────────┘
//
// On screens < 900px the sidebar collapses to a slide-out drawer triggered
// by a menu button in the top bar. State management (which tab is active)
// stays in the parent — this component is a controlled chrome.

// `AdminShell` is the generic dashboard shell — used by both the admin
// dashboard and the user dashboard. The name is kept for history; pass
// `brandText` to swap the sidebar header label.
const AdminShell = ({
  groups,           // [{ label, items: [{ key, label, icon?, badge? }] }]
  activeKey,
  onChange,
  title,            // current section title (string)
  topbarRight,      // React node — usually <NotificationBell />
  brandText = 'Admin Console',
  brandMark = 'PS',
  children,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const renderItem = (item) => {
    const active = item.key === activeKey;
    return (
      <button
        key={item.key}
        type="button"
        className={`admin-shell-nav-item ${active ? 'admin-shell-nav-item--active' : ''}`}
        onClick={() => { onChange(item.key); setDrawerOpen(false); }}
      >
        <span className="admin-shell-nav-item-icon" aria-hidden="true">
          {item.icon || '·'}
        </span>
        <span className="admin-shell-nav-item-label">{item.label}</span>
        {item.badge != null && Number(item.badge) > 0 && (
          <span className="admin-shell-nav-item-badge">{item.badge}</span>
        )}
      </button>
    );
  };

  return (
    <div className="admin-shell">
      {/* Sidebar */}
      <aside className={`admin-shell-sidebar ${drawerOpen ? 'admin-shell-sidebar--open' : ''}`}>
        <div className="admin-shell-sidebar-brand">
          <span className="admin-shell-sidebar-brand-mark">{brandMark}</span>
          <span className="admin-shell-sidebar-brand-text">{brandText}</span>
        </div>
        <nav className="admin-shell-nav">
          {groups.map((g) => (
            <div key={g.label} className="admin-shell-nav-group">
              <span className="admin-shell-nav-group-label">{g.label}</span>
              {g.items.map(renderItem)}
            </div>
          ))}
        </nav>
        <div className="admin-shell-sidebar-foot">
          <span>Powered by Phonestream</span>
        </div>
      </aside>

      {/* Backdrop for the mobile drawer */}
      {drawerOpen && (
        <div className="admin-shell-backdrop" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Main column */}
      <div className="admin-shell-main">
        <header className="admin-shell-topbar">
          <button
            type="button"
            className="admin-shell-menu-btn"
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="Toggle navigation"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6"  x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          </button>
          <div className="admin-shell-topbar-title">{title}</div>
          <div className="admin-shell-topbar-right">{topbarRight}</div>
        </header>
        <main className="admin-shell-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
